/**
 * Diagnostic overlay — modal with device status + 3 actions.
 *
 * Auto-dismisses after 20s idle (spec §10). Closes on visibilitychange
 * when the tab is hidden (safety — no stale modal on a background tab).
 */

import { DeviceController } from '../state/controller.js';
import { STORAGE_KEYS } from '../storage.js';

const IDLE_TIMEOUT_MS = 20_000;

export interface DiagnosticHandle {
  close(): void;
}

export function openDiagnostic(
  root: HTMLElement,
  controller: DeviceController
): DiagnosticHandle {
  const overlay = document.createElement('div');
  overlay.className = 'ag-diag';

  const card = document.createElement('div');
  card.className = 'ag-diag__card';
  overlay.appendChild(card);

  const title = document.createElement('h2');
  title.className = 'ag-diag__title';
  title.textContent = 'Diagnostic';
  card.appendChild(title);

  const rows = document.createElement('div');
  rows.className = 'ag-diag__rows';
  card.appendChild(rows);

  function row(label: string, value: string): void {
    const l = document.createElement('div');
    l.className = 'ag-diag__label';
    l.textContent = label;
    const v = document.createElement('div');
    v.className = 'ag-diag__value';
    v.textContent = value;
    rows.appendChild(l);
    rows.appendChild(v);
  }

  row('Device ID', controller.deviceId ?? '—');
  row('Organization', controller.orgId ?? '—');
  row('Device code', controller.storage.get(STORAGE_KEYS.deviceCode) ?? '—');
  row('Browser', controller.browserLabel);
  row('App version', controller.appVersion);
  row('State', controller.state);
  row('Mode', controller.effectiveMode);
  row('Last error', controller.lastError ?? '—');

  const actions = document.createElement('div');
  actions.className = 'ag-diag__actions';
  card.appendChild(actions);

  const closeFns: Array<() => void> = [];

  const dismiss = (): void => {
    for (const fn of closeFns) fn();
    overlay.remove();
  };

  const refreshBtn = button('Refresh config', false, async () => {
    await controller.debugRunCommand({
      id: `diag-${Date.now()}`,
      type: 'refresh',
    });
    dismiss();
  });
  const requestBtn = button('Request new code', false, async () => {
    await controller.wipe();
    dismiss();
  });
  const closeBtn = button('Close', true, () => dismiss());
  actions.append(refreshBtn, requestBtn, closeBtn);

  // Focus the Close button by default so the user can escape with one key.
  queueMicrotask(() => closeBtn.focus());

  // Idle auto-close
  const idleTimer = setTimeout(dismiss, IDLE_TIMEOUT_MS);
  closeFns.push(() => clearTimeout(idleTimer));

  // Tab-hidden auto-close
  const onVis = (): void => {
    if (document.hidden) dismiss();
  };
  document.addEventListener('visibilitychange', onVis);
  closeFns.push(() => document.removeEventListener('visibilitychange', onVis));

  // Escape key dismiss
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      dismiss();
    }
  };
  document.addEventListener('keydown', onKey);
  closeFns.push(() => document.removeEventListener('keydown', onKey));

  root.appendChild(overlay);

  return { close: dismiss };
}

function button(
  label: string,
  primary: boolean,
  onClick: () => void | Promise<void>
): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = primary ? 'ag-diag__btn ag-diag__btn--primary' : 'ag-diag__btn';
  b.type = 'button';
  b.textContent = label;
  b.addEventListener('click', () => {
    void onClick();
  });
  return b;
}
